import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Boxes, Settings, Inbox, KeyRound, X, Search, ChevronRight, Plug, CalendarClock, Users, Eye } from 'lucide-react';
import { cn } from '@/lib/cn';
import { CompanyLogo } from '@/components/CompanyLogo';
import { SwarmLockup, SupportedByWarriorLab } from './SwarmMark';
import { useIsStaff, useCanReview } from '@/app/store';
import { fetchPendingReviewCount } from '@/lib/reviewSources';
import { useAsync } from '@/lib/useAsync';
import { api } from '@/lib/api';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  end?: boolean;
  reviewerOnly?: boolean;
  badge?: 'review';
}

// One shell, role-scoped nav. Depth (company → workspace → project) lives in
// the URL and breadcrumbs, not the sidebar — so the nav stays flat and small.
// Console is for everyone (Review needs reviewer rights); Operations is the
// staff fleet view: the agent library, every schedule, every connector.
const CONSOLE_NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/review', label: 'Review', icon: Inbox, reviewerOnly: true, badge: 'review' },
  { to: '/settings', label: 'Settings', icon: Settings, end: true },
];

const OPERATIONS_NAV: NavItem[] = [
  // What the customer sees on this install — staff jump in to check or demo.
  { to: '/portal', label: 'Client portal', icon: Eye },
  { to: '/agents', label: 'Agents', icon: Boxes },
  { to: '/workflows', label: 'Workflows', icon: CalendarClock },
  { to: '/connectors', label: 'Connectors', icon: Plug },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/settings/claude', label: 'Platform', icon: KeyRound },
];

/** Pending review count for the nav badge — polled every 30s while mounted.
 * Errors show no badge (no badge beats a false zero). */
function usePendingReviewCount(enabled: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const tick = () => {
      fetchPendingReviewCount().then(
        (n) => alive && setCount(n),
        () => alive && setCount(0),
      );
    };
    tick();
    const timer = setInterval(tick, 30_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [enabled]);
  return count;
}

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const isStaff = useIsStaff();
  const canReview = useCanReview();
  const pendingCount = usePendingReviewCount(canReview);

  // The mobile drawer closes on Esc like every other overlay (backdrop + nav
  // clicks already close it). No-op while closed or in the static lg+ column.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={onClose} aria-hidden />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-[264px] flex-col overflow-hidden bg-shell pb-[env(safe-area-inset-bottom)]',
          'transition-transform lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="relative flex h-[76px] items-center justify-between px-5">
          <SwarmLockup tone="dark" />
          <button onClick={onClose} className="rounded-md p-1.5 text-shell-muted hover:bg-shell-2 hover:text-shell-fg lg:hidden" aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="relative flex-1 space-y-px overflow-y-auto overscroll-contain px-3 pb-3 pt-4">
          <p className="kicker mb-2 px-3 !text-shell-muted/70">Console</p>
          {CONSOLE_NAV.map((item) => (
            <SidebarLink key={item.to} item={item} canReview={canReview} pendingCount={pendingCount} onClose={onClose} />
          ))}

          {isStaff && (
            <div className="pt-5">
              <p className="kicker mb-2 px-3 !text-shell-muted/70">Operations</p>
              {OPERATIONS_NAV.map((item) => (
                <SidebarLink key={item.to} item={item} canReview={canReview} pendingCount={pendingCount} onClose={onClose} />
              ))}
            </div>
          )}

          <CompanyList onNavigate={onClose} />
        </nav>

        <div className="relative px-5 py-4">
          <SupportedByWarriorLab tone="dark" />
        </div>
      </aside>
    </>
  );
}

function SidebarLink({
  item,
  canReview,
  pendingCount,
  onClose,
}: {
  item: NavItem;
  canReview: boolean;
  pendingCount: number;
  onClose: () => void;
}) {
  if (item.reviewerOnly && !canReview) return null;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClose}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition',
          isActive ? 'bg-shell-2 text-white' : 'text-shell-muted hover:bg-shell-2/60 hover:text-shell-fg',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Active marker: a slim gradient blade, the report's accent. */}
          <span
            aria-hidden
            className={cn('absolute inset-y-1.5 left-0 w-[3px] rounded-full', isActive ? 'grad-bar' : 'bg-transparent')}
          />
          <item.icon className="h-4 w-4" strokeWidth={1.75} />
          {item.label}
          {item.badge === 'review' && pendingCount > 0 && (
            <span className="ml-auto rounded-sm bg-accent/90 px-1.5 py-0.5 text-2xs font-semibold text-white">
              {pendingCount}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/**
 * The client book as a monday-style rail: filter box, then a collapsible
 * company → workspace tree. Scales past a hundred companies because you type,
 * not scroll — the filter matches companies AND their workspaces.
 */
function CompanyList({ onNavigate }: { onNavigate: () => void }) {
  const overview = useAsync(() => api.getOverview(), []);
  // The sidebar sits above the company routes, so read the location directly.
  const { pathname } = useLocation();
  const match = pathname.match(/^\/c\/([^/]+)(?:\/w\/([^/]+))?/);
  const companySlug = match?.[1];
  const workspaceSlug = match?.[2];
  const [query, setQuery] = useState('');
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  if (overview.status !== 'success' || overview.data.companies.length === 0) return null;
  const companies = overview.data.companies;

  const q = query.trim().toLowerCase();
  const visible = q
    ? companies.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.workspaces.some((w) => w.name.toLowerCase().includes(q)),
      )
    : companies;

  const toggle = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="pt-5">
      <p className="kicker mb-2 px-3 !text-shell-muted/70">Companies</p>
      <div className="relative mb-1.5 px-1">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-shell-muted/60" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter…"
          className="h-8 w-full rounded-md border border-white/10 bg-white/5 pl-8 pr-2 text-[13px] text-shell-fg placeholder:text-shell-muted/60 focus:border-white/25 focus:outline-none"
        />
      </div>
      {visible.map((c) => {
        const isCurrent = companySlug === c.slug;
        // The filter and the current location both force a company open.
        const expanded = openIds.has(c.id) || isCurrent || (!!q && visible.length <= 4);
        return (
          <div key={c.id}>
            <div
              className={cn(
                'group relative flex items-center gap-1.5 rounded-md pr-2 text-[13px] font-medium transition',
                isCurrent && !workspaceSlug
                  ? 'bg-shell-2 text-white'
                  : 'text-shell-muted hover:bg-shell-2/60 hover:text-shell-fg',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'absolute inset-y-1.5 left-0 w-[3px] rounded-full',
                  isCurrent && !workspaceSlug ? 'grad-bar' : 'bg-transparent',
                )}
              />
              <button
                onClick={() => toggle(c.id)}
                aria-label={expanded ? 'Collapse' : 'Expand'}
                className="ml-1 rounded p-1 text-shell-muted/70 hover:text-shell-fg"
              >
                <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-90')} />
              </button>
              <NavLink
                to={`/c/${c.slug}`}
                onClick={onNavigate}
                className="flex min-w-0 flex-1 items-center gap-2 py-[7px]"
              >
                <CompanyLogo name={c.name} websiteUrl={c.websiteUrl} logoUrl={c.logoUrl} size={20} />
                <span className="truncate">{c.name}</span>
                {c.pendingSuggestions > 0 && (
                  <span className="ml-auto rounded-sm bg-accent/90 px-1 py-px text-2xs font-semibold text-white">
                    {c.pendingSuggestions}
                  </span>
                )}
              </NavLink>
            </div>
            {expanded &&
              c.workspaces
                .filter((w) => !q || w.name.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))
                .map((w) => (
                  <NavLink
                    key={w.id}
                    to={`/c/${c.slug}/w/${w.slug}`}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'relative ml-[26px] flex items-center gap-2 rounded-md py-[5px] pl-3 pr-2 text-[12.5px] transition',
                        'before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-white/10',
                        isActive
                          ? 'bg-shell-2 font-medium text-white'
                          : 'text-shell-muted/90 hover:bg-shell-2/60 hover:text-shell-fg',
                      )
                    }
                  >
                    <span className="truncate">{w.name}</span>
                  </NavLink>
                ))}
          </div>
        );
      })}
      {visible.length === 0 && (
        <p className="px-3 py-2 text-xs text-shell-muted/70">No matches.</p>
      )}
    </div>
  );
}
